import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import DonutChart from '@/components/ui/DonutChart';
import MetricCard from '@/components/ui/MetricCard';
import { useInstructorHighlights, InstructorHighlight } from '@/hooks/useInstructorHighlights';
import { Plus } from 'lucide-react';

const InstructorJourneyHighlightsSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { highlights: highlightsData, isLoading, error, refetch } = useInstructorHighlights();

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  // Calculate total number of pages based on visible cards
  const cardsPerView = 3; // Show 3 cards per view
  const totalCards = highlightsData ? 9 : 0; // 9 cards total
  const totalPages = Math.ceil(totalCards / cardsPerView);

  // Handle scroll to update current index
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 135; // w-[135px] = 135px
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.min(newIndex, totalPages - 1));
    }
  };

  // Scroll to specific page
  const scrollToPage = (pageIndex: number) => {
    if (scrollContainerRef.current) {
      const scrollLeft = pageIndex * cardWidth * cardsPerView;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const cardWidth = 135;

  const handleAddClasses = () => {
    navigate('/calendar');
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-gray-400">Loading highlights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          {t('home.journey_highlights.title')}
        </h2>
        <div className="text-red-400">Error loading highlights</div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-white font-roboto mb-4">
        {t('home.journey_highlights.title')}
      </h2>
      
      <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
        <div 
          className="w-full overflow-x-auto pb-2 scrollbar-hide" 
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div className="flex space-x-4">
            {/* Card 1: Total Classes Taught with Donut Chart */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px]">
              {/* Title */}
              <div className="mb-2">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Total Classes Taught</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Donut Chart with real data */}
              <DonutChart
                segments={[
                  {
                    label: 'Group',
                    value: highlightsData?.totalClasses.group || 0,
                    color: '#9852DB',
                    percentage: highlightsData?.totalClasses.total > 0 
                      ? (highlightsData.totalClasses.group / highlightsData.totalClasses.total) * 100 
                      : 0
                  },
                  {
                    label: 'Private',
                    value: highlightsData?.totalClasses.private || 0,
                    color: '#416FF0',
                    percentage: highlightsData?.totalClasses.total > 0 
                      ? (highlightsData.totalClasses.private / highlightsData.totalClasses.total) * 100 
                      : 0
                  }
                ]}
                total={highlightsData?.totalClasses.total || 0}
                size={64}
                strokeWidth={8}
                showCenterValue={true}
                showPercentages={true}
                showLegend={true}
              />
            </div>

            {/* Card 2: Revenue with Add Classes Button */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Revenue with your slots</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Revenue Value with real data */}
              <div className="text-center mb-4 flex-1 flex items-center justify-center">
                <div className="space-y-1">
                  <p className="text-[10px] font-normal text-white text-center">
                    {highlightsData?.revenue.currency || 'USD'}
                  </p>
                  <p className="text-[28px] font-bold text-white leading-none">
                    {highlightsData?.revenue.amount?.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    }) || '0.00'}
                  </p>
                </div>
              </div>
              
              {/* Add Classes Button */}
              <div className="flex justify-center">
                <button 
                  onClick={handleAddClasses}
                  className="bg-transparent border border-white rounded-[100px] flex items-center justify-center gap-2 px-3 py-2 hover:bg-white/10 transition-colors w-full"
                >
                  <Plus className="w-3 h-3 text-white" />
                  <span className="text-[10px] font-bold text-white">Add Classes</span>
                </button>
              </div>
            </div>

            {/* Card 3: Most Active Student */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px]">
              {/* Title */}
              <div className="mb-2">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Most Active Student</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Student Avatar with FramedAvatar */}
              <div className="flex justify-center mb-3">
                {highlightsData?.mostActiveStudent.name && highlightsData.mostActiveStudent.name !== 'No students yet' ? (
                  <FramedAvatar
                    src={highlightsData.mostActiveStudent.avatarUrl}
                    alt={highlightsData.mostActiveStudent.name}
                    initials={highlightsData.mostActiveStudent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    size={36}
                    frameSize={{ w: 48, h: 52}}
                    beltRank={highlightsData.mostActiveStudent.beltRank || "white"}
                    fontSize={14}
                  />
                ) : (
                  <FramedAvatar
                    initials="?"
                    size={36}
                    frameSize={{ w: 48, h: 52}}
                    beltRank="white"
                    fontSize={14}
                  />
                )}
              </div>
              
              {/* Student Name with real data */}
              <div className="text-center mb-2">
                <p className="text-[12px] font-bold text-white font-roboto leading-[20px]">
                  {highlightsData?.mostActiveStudent.name || 'No students yet'}
                </p>
              </div>
              
              {/* Classes Count with real data */}
              <div className="text-center">
                <p className="text-[10px] text-neutral-300 font-normal leading-[16px] tracking-[-0.5px]">
                  <span className="font-bold text-white">
                    {highlightsData?.mostActiveStudent.classesCount || 0}
                  </span> classes together!
                </p>
              </div>
            </div>

            {/* Card 4: Mat Time Utilization */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Mat Time Utilization</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Utilization Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.matUtilization.percentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    utilization
                  </div>
                </div>
              </div>
              
              {/* Utilization Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.matUtilization.occupiedSlots || 0}</span>h occupied
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.matUtilization.totalSlots || 0}</span>h available
                </div>
              </div>
            </div>

            {/* Card 5: Booking Efficiency */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Booking Efficiency</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Efficiency Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.bookingEfficiency.percentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    efficiency
                  </div>
                </div>
              </div>
              
              {/* Efficiency Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.bookingEfficiency.completedBookings || 0}</span> completed
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="text-[10px] font-bold">{highlightsData?.bookingEfficiency.totalBookings || 0}</span> total
                </div>
              </div>
            </div>

            {/* Card 6: Total Hours on the Mat */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Total Hours on the Mat</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Total Hours - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.totalHours.hours || 0}
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    hours
                  </div>
                </div>
              </div>
              
              {/* Time Details - Bottom */}
              <div className="text-center">
                <div className="text-[10px] text-white font-normal opacity-70">
                  Teaching time
                </div>
              </div>
            </div>

            {/* Card 7: Most Requested Technique */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Most Requested Technique</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Technique Icon - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[32px] mb-2">🥋</div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    technique
                  </div>
                </div>
              </div>
              
              {/* Technique Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[12px] font-bold text-white font-roboto">
                  {highlightsData?.mostRequestedTechnique.name || 'No techniques yet'}
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.mostRequestedTechnique.count || 0}</span> requests
                </div>
              </div>
            </div>

            {/* Card 8: Schedule Status */}
            <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
              {/* Title */}
              <div className="mb-3">
                <p className="text-[12px] font-semibold text-white font-roboto mb-0">Your Schedule Status</p>
                <p className="text-[10px] font-light text-white font-roboto">this month</p>
              </div>
              
              {/* Status Percentage - Centralized and Larger */}
              <div className="text-center flex-1 flex items-center justify-center">
                <div className="space-y-2">
                  <div className="text-[36px] font-bold text-white leading-none">
                    {highlightsData?.scheduleStatus.completedPercentage || 0}%
                  </div>
                  <div className="text-[14px] font-medium text-white opacity-80">
                    completed
                  </div>
                </div>
              </div>
              
              {/* Status Details - Bottom */}
              <div className="text-center space-y-1">
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.scheduleStatus.completed || 0}</span> completed
                </div>
                <div className="text-[10px] text-white font-normal opacity-70">
                  <span className="font-bold">{highlightsData?.scheduleStatus.missed || 0}</span> missed
                </div>
              </div>
            </div>

            {/* Card 9: Achievement Milestone (show first reached milestone) */}
            {highlightsData?.achievements?.find(a => a.reached) && (
              <div className="flex-shrink-0 bg-[#2d2424] rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] relative p-4 min-w-[135px] flex flex-col justify-between">
                {/* Title */}
                <div className="mb-3">
                  <p className="text-[12px] font-semibold text-white font-roboto mb-0">Achievement Unlocked!</p>
                  <p className="text-[10px] font-light text-white font-roboto">milestone reached</p>
                </div>
                
                {/* Achievement Icon - Centralized and Larger */}
                <div className="text-center flex-1 flex items-center justify-center">
                  <div className="space-y-2">
                    <div className="text-[32px] mb-2">
                      {highlightsData.achievements.find(a => a.reached)?.icon || '🏆'}
                    </div>
                    <div className="text-[14px] font-medium text-white opacity-80">
                      unlocked!
                    </div>
                  </div>
                </div>
                
                {/* Achievement Details - Bottom */}
                <div className="text-center space-y-1">
                  <div className="text-[12px] font-bold text-white font-roboto">
                    {highlightsData.achievements.find(a => a.reached)?.title || 'Milestone'}
                  </div>
                  <div className="text-[10px] text-white font-normal opacity-70">
                    {highlightsData.achievements.find(a => a.reached)?.description || 'Congratulations!'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation dots */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-1.5 mt-4">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              onClick={() => scrollToPage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-gray-300 scale-125' 
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InstructorJourneyHighlightsSection;
